import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuth, fetchAndDownload } from '../../utils/api';
import ConfirmationModal from '../common/ConfirmationModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function CustomersView({ user, handleApiError, showNotification }) {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    
    const [isEditMode, setIsEditMode] = useState(false);
    const [activeCustomer, setActiveCustomer] = useState(null);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All Status');
    const [sortBy, setSortBy] = useState('Recently Added');

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        const params = new URLSearchParams({
            search: searchTerm,
            status: statusFilter,
            sortBy: sortBy
        });
        try {
            const res = await fetchAuth(`${API_URL}/api/customers?${params.toString()}`);
            if (res.ok) setCustomers(await res.json());
        } catch (error) { 
            handleApiError(error); 
        } finally {
            setLoading(false);
        }
    }, [handleApiError, searchTerm, statusFilter, sortBy]);

     useEffect(() => {
        const timer = setTimeout(() => {
            fetchCustomers();
        }, 500);
        return () => clearTimeout(timer);
    }, [fetchCustomers]);

    const handleExport = () => {
        fetchAndDownload(`${API_URL}/api/customers/export`, 'customers.csv');
    };

    const handleAddCustomer = async (newCustomer) => {
        try {
            const res = await fetchAuth(`${API_URL}/api/customers`, { method: 'POST', body: JSON.stringify(newCustomer) });
            if (!res.ok) throw new Error((await res.json()).message || 'Failed to add customer');
            showNotification('Customer added successfully!', 'success');
            setIsFormModalOpen(false);
            fetchCustomers();
        } catch (err) { handleApiError(err); }
    };

    const handleUpdateCustomer = async (customerId, updatedData) => {
        try {
            const res = await fetchAuth(`${API_URL}/api/customers/${customerId}`, { method: 'PUT', body: JSON.stringify(updatedData) });
            if (!res.ok) throw new Error((await res.json()).message || 'Failed to update customer');
            showNotification('Customer updated successfully!', 'success');
            setIsFormModalOpen(false);
            fetchCustomers();
        } catch (err) { handleApiError(err); }
    };

    const handleDeleteCustomer = async () => {
        if (!activeCustomer) return;
        try {
            const res = await fetchAuth(`${API_URL}/api/customers/${activeCustomer._id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error((await res.json()).message || 'Failed to delete customer');
            showNotification('Customer deleted successfully!', 'success');
            fetchCustomers();
        } catch (err) {
            handleApiError(err);
        } finally {
            setIsConfirmModalOpen(false);
            setActiveCustomer(null);
        }
    };
    
    const openFormModal = (customer = null) => {
        if (customer) {
            setIsEditMode(true);
            setActiveCustomer(customer);
        } else {
            setIsEditMode(false);
            setActiveCustomer(null);
        }
        setIsFormModalOpen(true);
    };

   const openConfirmModal = (customer) => {
        setActiveCustomer(customer);
        setIsConfirmModalOpen(true);
    };

    const CustomerModal = ({ onClose, onSave, isEdit, customerData }) => {
        const [customer, setCustomer] = useState({});

        useEffect(() => {
            if (isEdit && customerData) {
                setCustomer(customerData);
            } else {
                setCustomer({ name: '', phone: '', email: '', address: '' });
            }
        }, [isEdit, customerData]);

        const handleChange = (e) => setCustomer(prev => ({ ...prev, [e.target.name]: e.target.value }));
        
        const handleSubmit = (e) => {
            e.preventDefault();
            onSave(customer);
        };

        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <h3>{isEdit ? 'Edit Customer' : 'Add New Customer'}</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="input-group"><label>Customer Name</label><input name="name" value={customer.name || ''} onChange={handleChange} required /></div>
                        <div className="input-group"><label>Phone Number</label><input name="phone" value={customer.phone || ''} onChange={handleChange} required /></div>
                        <div className="input-group"><label>Email (Optional)</label><input name="email" type="email" value={customer.email || ''} onChange={handleChange} /></div>
                        <div className="input-group"><label>Address (Optional)</label><textarea name="address" value={customer.address || ''} onChange={handleChange} rows="3"></textarea></div>
                        <div className="modal-actions">
                            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                            <button type="submit" className="confirm-btn">Save Changes</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    if (loading) return <div>Loading customers...</div>;

    return (
        <div className="page-container">
            {isFormModalOpen && (
                <CustomerModal 
                    onClose={() => setIsFormModalOpen(false)}
                    onSave={(data) => isEditMode ? handleUpdateCustomer(activeCustomer._id, data) : handleAddCustomer(data)}
                    isEdit={isEditMode}
                    customerData={activeCustomer}
                />
            )}
            {isConfirmModalOpen && (
                <ConfirmationModal
                    onCancel={() => setIsConfirmModalOpen(false)}
                    onConfirm={handleDeleteCustomer}
                    customerName={activeCustomer?.name}
                />
            )}

            <div className="page-header">
                <div>
                    <h2 className="page-title">Customer Management</h2>
                    <p className="page-subtitle">Manage your customers and relationships.</p>
                </div>
                <div className="header-actions">
                    <button className="btn-export" onClick={handleExport}>
                        <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"></path></svg>
                        Export Customers
                    </button>
                    <button className="btn-primary" onClick={() => openFormModal()}>
                        <svg viewBox="0 0 24 24"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"></path></svg>
                        Add Customer
                    </button>
                </div>
            </div>

            <div className="filter-bar">
                <input type="text" className="search-input" placeholder="Search by name or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option>All Status</option>
                    <option>Active</option>
                    <option>Inactive</option>
                </select>
                <select className="filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                    <option>Recently Added</option>
                    <option>Name</option>
                </select>
            </div>
            
            <div className="data-display">
                {loading ? (
                    <p>Loading customers...</p>
                ) : customers.length > 0 ? (
                    <div className="inventory-table-container">
                        <table className="inventory-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Phone</th>
                                    <th>Email</th>
                                    <th>Address</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.map(c => (
                                    <tr key={c._id}>
                                        <td>{c.name}</td>
                                        <td>{c.phone}</td>
                                        <td>{c.email || 'N/A'}</td>
                                        <td>{c.address || 'N/A'}</td>
                                        <td><span className={`status-badge status-${c.status?.toLowerCase()}`}>{c.status}</span></td>
                                        <td>
                                            <div className="table-actions">
                                                <button className="icon-btn edit" onClick={() => openFormModal(c)}>
                                                    <svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path></svg>
                                                </button>
                                                <button className="icon-btn delete" onClick={() => openConfirmModal(c)}>
                                                    <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="no-data-placeholder">
                        <div className="placeholder-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"></path></svg>
                        </div>
                        <h3>No customers found</h3>
                        <p>Adjust your filters or add a new customer.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CustomersView;
